import { EventEmitter } from 'eventemitter3';
import { registerScopedServiceWorker } from '../../../utils/serviceWorker';
import { OgrafRenderer } from './OgrafRenderer';
import { OgrafStore } from './OgrafStore';
import { OgrafTrack } from './OgrafTrack';
export class OgrafManager extends EventEmitter {
    static instance;
    static getInstance() {
        if (!OgrafManager.instance) {
            OgrafManager.instance = new OgrafManager();
        }
        return OgrafManager.instance;
    }
    store;
    graphics = new Map();
    serviceWorkerRegistered = false;
    mediaManager;
    constructor() {
        super();
        this.store = new OgrafStore();
    }
    setMediaManager(mediaManager) {
        this.mediaManager = mediaManager;
    }
    async initialize() {
        await this.registerServiceWorker();
    }
    async registerServiceWorker() {
        if (this.serviceWorkerRegistered)
            return;
        if (!('serviceWorker' in navigator)) {
            console.warn('[OgrafManager] Service Workers not supported, OGraf asset serving will not work');
            return;
        }
        try {
            // the scope /ograf-assets/ never covers the page, see the helper
            await registerScopedServiceWorker('/ograf-asset-worker.js', '/ograf-assets/');
            this.serviceWorkerRegistered = true;
        }
        catch (err) {
            console.error('[OgrafManager] Failed to register service worker', err);
        }
    }
    async addGraphic(source) {
        let files;
        try {
            if (source instanceof File) {
                files = await OgrafStore.extractZip(source);
            }
            else {
                files = await OgrafStore.readDirectory(source);
            }
        }
        catch (err) {
            this.emit('error', null, `Failed to read graphic package: ${err}`);
            return;
        }
        const manifestEntry = this.findManifestInFiles(files);
        if (!manifestEntry) {
            this.emit('error', null, 'No .ograf.json manifest file found in the package');
            return;
        }
        let manifest;
        try {
            manifest = JSON.parse(manifestEntry.content);
        }
        catch {
            this.emit('error', null, 'Failed to parse .ograf.json manifest');
            return;
        }
        if (!manifest.id || !manifest.name || !manifest.main) {
            this.emit('error', null, 'Manifest missing required fields: id, name, or main');
            return;
        }
        const packageId = manifest.id;
        if (this.graphics.has(packageId)) {
            this.emit('error', packageId, `Graphic "${manifest.name}" is already loaded`);
            return;
        }
        // If manifest is in a subdirectory, rebase file paths
        const manifestDir = manifestEntry.path.includes('/')
            ? manifestEntry.path.substring(0, manifestEntry.path.lastIndexOf('/') + 1)
            : '';
        const rebasedFiles = new Map();
        for (const [path, data] of files) {
            const newPath = manifestDir && path.startsWith(manifestDir)
                ? path.substring(manifestDir.length)
                : path;
            rebasedFiles.set(newPath, data);
        }
        try {
            await this.store.storePackage(packageId, rebasedFiles);
        }
        catch (err) {
            this.emit('error', packageId, `Failed to store package to OPFS: ${err}`);
            return;
        }
        await this.registerServiceWorker();
        const trackId = `ograf:${packageId}`;
        const renderer = new OgrafRenderer(packageId, manifest);
        const track = new OgrafTrack(trackId);
        renderer.setFrameCallback((bitmap) => {
            track.feedFrame(bitmap);
        });
        const record = {
            manifest,
            renderer,
            track,
            state: 'loading',
        };
        this.graphics.set(packageId, record);
        try {
            await renderer.initialize();
            await renderer.load(extractSchemaDefaults(manifest.schema));
            record.state = 'ready';
            renderer.startFullRateCapture();
            renderer.stopFullRateCapture();
            if (this.mediaManager) {
                this.mediaManager.addOgrafTrack(track);
            }
            this.emit('graphicAdded', this.toEntry(packageId, record));
            this.emit('graphicStateChanged', packageId, 'ready');
        }
        catch (err) {
            record.state = 'error';
            this.emit('error', packageId, `Failed to initialize graphic: ${err}`);
            renderer.dispose();
            this.graphics.delete(packageId);
        }
    }
    async removeGraphic(id) {
        const record = this.graphics.get(id);
        if (!record)
            return;
        const trackId = `ograf:${id}`;
        if (this.mediaManager) {
            this.mediaManager.removeOgrafTrack(trackId);
        }
        record.renderer.dispose();
        record.track.destroy();
        this.graphics.delete(id);
        try {
            await this.store.removePackage(id);
        }
        catch {
            // best effort
        }
        this.emit('graphicRemoved', id);
    }
    async playGraphic(id, params) {
        const record = this.graphics.get(id);
        if (!record)
            return;
        const result = await record.renderer.playAction(params);
        record.state = 'playing';
        record.currentStep = result?.currentStep;
        this.emit('graphicStateChanged', id, 'playing', record.currentStep);
    }
    async stopGraphic(id, params) {
        const record = this.graphics.get(id);
        if (!record)
            return;
        await record.renderer.stopAction(params);
        record.state = 'stopped';
        record.currentStep = undefined;
        this.emit('graphicStateChanged', id, 'stopped');
    }
    async updateGraphic(id, data) {
        const record = this.graphics.get(id);
        if (!record)
            return;
        await record.renderer.updateAction({ data });
    }
    async customAction(id, actionId, payload) {
        const record = this.graphics.get(id);
        if (!record)
            return;
        await record.renderer.customAction({ id: actionId, payload });
    }
    getTrack(id) {
        return this.graphics.get(id)?.track;
    }
    getGraphics() {
        const entries = [];
        for (const [id, record] of this.graphics) {
            entries.push(this.toEntry(id, record));
        }
        return entries;
    }
    async destroy() {
        for (const [, record] of this.graphics) {
            record.renderer.dispose();
            record.track.destroy();
        }
        this.graphics.clear();
    }
    toEntry(id, record) {
        return {
            id,
            name: record.manifest.name,
            manifest: record.manifest,
            state: record.state,
            currentStep: record.currentStep,
        };
    }
    findManifestInFiles(files) {
        for (const [path, data] of files) {
            if (path.endsWith('.ograf.json')) {
                const content = new TextDecoder().decode(data);
                return { path, content };
            }
        }
        return undefined;
    }
}
/**
 * Builds the initial data object for a graphic from the `default` values
 * declared in its manifest schema, so graphics render their default
 * content (e.g. label texts) when loaded without user-provided data.
 */
function extractSchemaDefaults(schema) {
    if (typeof schema !== 'object' ||
        schema === null ||
        !('properties' in schema)) {
        return undefined;
    }
    const properties = schema.properties;
    if (typeof properties !== 'object' || properties === null) {
        return undefined;
    }
    const data = {};
    for (const [key, prop] of Object.entries(properties)) {
        if (typeof prop !== 'object' || prop === null)
            continue;
        if ('default' in prop) {
            data[key] = prop.default;
        }
        else if ('properties' in prop) {
            const nested = extractSchemaDefaults(prop);
            if (nested !== undefined)
                data[key] = nested;
        }
    }
    return Object.keys(data).length > 0 ? data : undefined;
}
